/**
 *  FELUGRÓ ABLAKOK MAGASSÁGA A TARTALOMHOZ (görgetés nélkül a Mentés gombig)
 *  ============================================================================
 *
 *  A user (2026-11-03): „a popup ablakok méretei nem teljesen jók, sok helyen görgetnem kell a mentés
 *  gombig, jó lenne ha görgetésmentes lenne mindegyik felugró ablak”.
 *
 *  A modálok kétféle elrendezésűek:
 *   – TARTALOMHOZ MÉRETEZETT (--height: auto; sorablak-, nk-, ber-urlap-, bevsor-, rlb-export-modal):
 *     ezeket a global.scss kezeli – a modál a tartalom magasságáig nő (legfeljebb 92vh), csak a törzs
 *     görget, a gombsor (sa-lab / nk-lab) a törzs alján ragad;
 *   – RÖGZÍTETT MAGASSÁGÚ (Ionic-alap 600×500/600, kozep-modal, osztály nélküli modálok): a tartalmuk
 *     ion-content, ami CSS-ből nem méretezhető a tartalomhoz (a belső görgető abszolút pozicionált).
 *     Ezeknél MÉRÜNK: ha a tartalom nem fér el, a modál `--height`-ja a tartalom magasságára nő
 *     (legfeljebb az ablak 92%-áig). Csak NŐ, a CSS-beli méret alá sosem megy – így a szándékosan nagy,
 *     belül görgető listás ablakok nem változnak. Később betöltődő tartalom (adatlekérés, fülváltás)
 *     után újramér.
 *
 *  Az asztali program külön ablakainál (ablak-modal.ts) ugyanez a mérés adja az OS-ablak magasságát
 *  (a képernyő magasságáig).
 */

const MAX_ARANY = 0.92;

/*  A FIGYELŐK AZ ANGULAR ZÓNÁJÁN KÍVÜL (2026-11-05, a user: „ha megnyitom a jelenlét rögzítő oldalt … lefagy”).
 *  A zónában futó MutationObserver / ResizeObserver / requestAnimationFrame minden visszahívása után az Angular
 *  változásészlelést futtat. Ha a modál sablonja minden észleléskor új tömböt ad egy *ngFor-nak (pl. egy getter),
 *  a DOM újraépül → a MutationObserver újra jelez → újabb észlelés … végtelen ciklus, a lap lefagy. A méretezésnek
 *  az Angular állapotához nincs köze (csak stílust ír), ezért a figyelőket a gyökérzónában hozzuk létre. */
export function zonanKivul<T>(fn: () => T): T {
  const Z = (window as any).Zone;
  return Z?.root && Z.current !== Z.root ? Z.root.run(fn) : fn();
}

/** Az elem görgethető-e függőlegesen (overflow-y: auto / scroll). */
function gorgeto(el: Element, ablak: Window): boolean {
  const o = ablak.getComputedStyle(el).overflowY;
  return o === 'auto' || o === 'scroll';
}

/**
 *  A modál-oldal (.ion-page) TERMÉSZETES magassága: a fejléc + a törzs teljes tartalma + a lábléc.
 *  A törzs (ion-content belső görgetője, vagy a közvetlen gyerek görgető div – .nk-torzs, .sa-torzs…)
 *  a látható magassága HELYETT a teljes tartalmával számít. A mélyebben fekvő, szándékosan korlátozott
 *  görgetők (pl. max-height-es táblázat) nem számítanak – azok a helyükön görgetnek tovább.
 */
export function termeszetesMagassag(page: HTMLElement): number {
  const ablak = page.ownerDocument.defaultView || window;
  let h = 0;
  for (const c of Array.from(page.children) as HTMLElement[]) {
    const cs = ablak.getComputedStyle(c);
    if (cs.display === 'none' || cs.position === 'absolute' || cs.position === 'fixed') continue;
    let ch = c.offsetHeight;
    if (c.tagName === 'ION-CONTENT') {
      const sc = c.shadowRoot?.querySelector('.inner-scroll') as HTMLElement | null;
      if (sc) ch += Math.max(0, sc.scrollHeight - sc.clientHeight);
      //  Az ion-content egyetlen, kitöltő görgető gyereke (pl. height:100% + overflow:auto burok).
      const gy = Array.from(c.children) as HTMLElement[];
      if (gy.length === 1 && gorgeto(gy[0], ablak) && gy[0].offsetHeight >= c.offsetHeight - 40) {
        ch += Math.max(0, gy[0].scrollHeight - gy[0].clientHeight);
      }
    } else if (gorgeto(c, ablak)) {
      ch += Math.max(0, c.scrollHeight - c.clientHeight);
    }
    h += ch + (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
  }
  return Math.ceil(h);
}

/** A modál tartalma (.ion-page): inline modálnál a burok div, vezérlővel nyitottnál a komponens. */
export function modalOldal(gyoker: Element): HTMLElement | null {
  return (Array.from(gyoker.children).find((c) => c.classList.contains('ion-page')) as HTMLElement) || null;
}

/**
 *  Figyeli a tartalom változását (méret, DOM), és ilyenkor (képkockánként legfeljebb egyszer) meghívja
 *  a `fn`-t. A visszaadott függvény leállítja a figyelést.
 */
export function tartalomFigyel(page: HTMLElement, fn: () => void): () => void {
  return zonanKivul(() => tartalomFigyelZonaban(page, fn));
}

function tartalomFigyelZonaban(page: HTMLElement, fn: () => void): () => void {
  const ablak = page.ownerDocument.defaultView || window;
  let kert = false;
  const utem = () => { if (kert) return; kert = true; ablak.requestAnimationFrame(() => { kert = false; fn(); }); };
  const RO = (ablak as any).ResizeObserver || ResizeObserver;
  const ro: ResizeObserver = new RO(utem);
  const figyeltek = new Set<Element>();
  const feliratkoz = () => {
    //  A törzs (és az ion-content) közvetlen gyerekei: ezek nőnek, ha betöltődik / kinyílik valami.
    for (const c of Array.from(page.children)) {
      const lista = c.tagName === 'ION-CONTENT' || gorgeto(c, ablak) ? Array.from(c.children) : [c];
      for (const e of lista) if (!figyeltek.has(e)) { figyeltek.add(e); ro.observe(e); }
    }
  };
  feliratkoz();
  const MO = (ablak as any).MutationObserver || MutationObserver;
  const mo: MutationObserver = new MO(() => { feliratkoz(); utem(); });
  mo.observe(page, { childList: true, subtree: true });
  return () => { ro.disconnect(); mo.disconnect(); };
}

/* ── WEB: a főablakbeli (rétegként nyíló) modálok ─────────────────────────────────────────────── */

function illeszt(modal: HTMLElement) {
  //  Asztali programban külön ablakba költözött: ott az ablak-modal.ts méretez.
  if (modal.dataset['ablakModal']) return;
  const wrapper = modal.shadowRoot?.querySelector('.modal-wrapper') as HTMLElement | null;
  const page = modalOldal(modal);
  if (!wrapper || !page) return;
  //  A tartalomhoz méretezett (--height: auto) modálokat a CSS kezeli (global.scss).
  if (/^(auto|fit-content)$/.test(getComputedStyle(modal).getPropertyValue('--height').trim())) return;
  const max = Math.floor(window.innerHeight * MAX_ARANY);
  const most = wrapper.getBoundingClientRect().height;
  if (most >= max - 1) return;                           // már a lehető legnagyobb (vagy teljes képernyő)
  const kell = termeszetesMagassag(page);
  if (kell <= most + 2) return;
  modal.style.setProperty('--height', `${Math.min(kell, max)}px`, 'important');
}

export function modalIllesztesIndit(): void {
  const leallit = new WeakMap<HTMLElement, () => void>();
  document.addEventListener('ionModalDidPresent', (e) => {
    const modal = e.target as HTMLElement;
    //  Lapozós (sheet) és kártya-modál: szándékos méret, nem bántjuk.
    if (!modal || modal.classList.contains('modal-sheet') || modal.classList.contains('modal-card')) return;
    //  Az ablak-modal.ts ugyanerre az eseményre költöztet; utána fusson (a jelzése már fenn van).
    setTimeout(() => {
      if (modal.dataset['ablakModal']) return;
      const page = modalOldal(modal);
      if (!page) return;
      illeszt(modal);
      leallit.get(modal)?.();
      leallit.set(modal, tartalomFigyel(page, () => illeszt(modal)));
    });
  }, true);
  document.addEventListener('ionModalDidDismiss', (e) => {
    const modal = e.target as HTMLElement;
    leallit.get(modal)?.(); leallit.delete(modal);
    //  Az újranyíló (sablonban álló) modál újra a saját CSS-méretéről induljon.
    modal?.style?.removeProperty('--height');
  }, true);
  window.addEventListener('resize', () => {
    document.querySelectorAll('ion-modal.show-modal').forEach((m) => {
      (m as HTMLElement).style.removeProperty('--height');
      illeszt(m as HTMLElement);
    });
  });
}
